package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import com.castly.shared.dto.SectionTranslationData
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Continue Watching Section Editor
 *
 * Simple editor for continue_watching and continue_watching_carousel section types.
 * These sections are auto-populated per-user from stream progress data, so they only
 * need an internal name and bilingual translations (title/description).
 */
@Composable
fun ContinueWatchingSectionEditor(
    section: AdminSectionDto?,
    sectionType: String,
    onDataChanged: (String, List<SectionTranslationData>) -> Unit = { _, _ -> }
) {
    val colors = AppTheme.colors

    var internalName by remember { mutableStateOf(section?.internalName ?: "") }
    var titleEn by remember { mutableStateOf(section?.translations?.find { it.languageCode == "eng" }?.title ?: "") }
    var titleAr by remember { mutableStateOf(section?.translations?.find { it.languageCode == "ara" }?.title ?: "") }
    var descEn by remember { mutableStateOf(section?.translations?.find { it.languageCode == "eng" }?.description ?: "") }
    var descAr by remember { mutableStateOf(section?.translations?.find { it.languageCode == "ara" }?.description ?: "") }

    // Notify parent on any change
    LaunchedEffect(internalName, titleEn, titleAr, descEn, descAr) {
        val translations = listOf(
            SectionTranslationData(languageCode = "eng", title = titleEn, description = descEn.ifBlank { null }),
            SectionTranslationData(languageCode = "ara", title = titleAr, description = descAr.ifBlank { null })
        )
        onDataChanged(internalName, translations)
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {
        // Info banner
        Div({
            style {
                padding(Spacing.md)
                backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#dbeafe" else "#1e3a5f"))
                color(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#1e40af" else "#93c5fd"))
                borderRadius(BorderRadius.sm)
                marginBottom(Spacing.xl)
                fontSize(Typography.base)
                themeTransition()
            }
        }) {
            if (sectionType == "continue_watching") {
                Text("This section shows a single hero card with the user's most recent in-progress episode. Content is auto-populated per user.")
            } else {
                Text("This section shows a scrollable carousel of the user's in-progress episodes (up to 5). Content is auto-populated per user.")
            }
        }

        // Internal Name
        Div({ style { marginBottom(Spacing.lg) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                Text("Internal Name")
                Span({ style { color(Color(colors.danger)); marginLeft(Spacing.xs) } }) { Text("*") }
            }
            Input(InputType.Text) {
                attr("type", "text")
                attr("value", internalName)
                attr("placeholder", "e.g., Continue Watching Hero")
                onInput { internalName = it.value }
                style {
                    width(100.percent)
                    padding(Spacing.sm)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    property("box-sizing", "border-box")
                }
            }
        }

        // Translations header
        H3({ style { marginTop(Spacing.xl); marginBottom(Spacing.lg); color(Color(colors.text)) } }) {
            Text("Translations")
        }

        // English translations
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.sm)
                marginBottom(Spacing.lg)
                themeTransition()
            }
        }) {
            H4({ style { marginTop(0.px); marginBottom(Spacing.md); color(Color(colors.text)) } }) {
                Text("English")
            }

            Div({ style { marginBottom(Spacing.md) } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Title")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", titleEn)
                    attr("placeholder", "e.g., Continue Watching")
                    onInput { titleEn = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }

            Div {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Description (optional)")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", descEn)
                    attr("placeholder", "e.g., Pick up where you left off")
                    onInput { descEn = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
        }

        // Arabic translations
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surfaceHover))
                borderRadius(BorderRadius.sm)
                themeTransition()
            }
        }) {
            H4({ style { marginTop(0.px); marginBottom(Spacing.md); color(Color(colors.text)) } }) {
                Text("Arabic")
            }

            Div({ style { marginBottom(Spacing.md) } }) {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Title")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", titleAr)
                    attr("placeholder", "e.g., \u0623\u0643\u0645\u0644 \u0627\u0644\u0645\u0634\u0627\u0647\u062F\u0629")
                    attr("dir", "rtl")
                    onInput { titleAr = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }

            Div {
                Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                    Text("Description (optional)")
                }
                Input(InputType.Text) {
                    attr("type", "text")
                    attr("value", descAr)
                    attr("placeholder", "e.g., \u0623\u0643\u0645\u0644 \u0645\u0646 \u062D\u064A\u062B \u062A\u0648\u0642\u0641\u062A")
                    attr("dir", "rtl")
                    onInput { descAr = it.value }
                    style {
                        width(100.percent)
                        padding(Spacing.sm)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.sm)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        property("box-sizing", "border-box")
                    }
                }
            }
        }
    }
}
